/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { Conditions } from '@vsolv/packages/conditions/domain';
import { Property, PropertySet } from '@vsolv/packages/properties/domain';
import { getPropertyValidators } from '@vsolv/packages/properties/web';
import { DIMENSION_VALUE_NAMESPACE } from '@wsphere/warranties/domain';
import { Element, RatingMatrixService } from '../../services';
import {
  isMissingProperty,
  MISSING_PROPERTY_MESSAGE,
} from '../condition-property-picker/condition-property-picker.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-condition-builder',
  templateUrl: './condition-builder.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ConditionBuilderComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: ConditionBuilderComponent,
    },
  ],
})
export class ConditionBuilderComponent implements OnInit, ControlValueAccessor, Validator {
  constructor(
    private formBuilder: FormBuilder,
    private injector: Injector,
    private ratingMatrixSvc: RatingMatrixService
  ) {
    this.form.valueChanges.subscribe(value => this.onChange(value as any));
  }

  @Output() conditionRemoved = new EventEmitter<null>();

  @Input() propertySet!: PropertySet.Model;
  @Input() includeDimensions = false;

  touched = false;
  control?: NgControl;

  properties: Conditions.Property[] = [];
  filteredProperties = this.properties;
  selectedProperty: Conditions.Property | Property.Model<Property.PropertyType> | null = null;

  categoricalDimensionElements: Element[] = [];
  selectedPropertyIsCategoricalDimension = false;
  selectedPropertyIsMissing = false;
  readonly missingPropertyMessage = MISSING_PROPERTY_MESSAGE;

  bool = Conditions.PropertyType.BOOLEAN;
  text = Conditions.PropertyType.TEXT;
  num = Conditions.PropertyType.NUMBER;
  date = Conditions.PropertyType.DATE;

  isNull = Conditions.Operator.IS_NULL;
  isNotNull = Conditions.Operator.IS_NOT_NULL;

  generalOperators = [
    Conditions.Operator.EQUALS,
    Conditions.Operator.GREATER_THAN,
    Conditions.Operator.GREATER_THAN_EQUALS,
    Conditions.Operator.LESS_THAN,
    Conditions.Operator.LESS_THAN_EQUALS,
    Conditions.Operator.NOT_EQUALS,
    Conditions.Operator.IS_NULL,
    Conditions.Operator.IS_NOT_NULL,
  ];
  textOperatorGroups: { label: string; operators: Conditions.Operator[] }[] = [
    {
      label: 'Equality',
      operators: [Conditions.Operator.EQUALS, Conditions.Operator.NOT_EQUALS],
    },
    {
      label: 'Substring',
      operators: [
        Conditions.Operator.CONTAINS,
        Conditions.Operator.NOT_CONTAINS,
        Conditions.Operator.STARTS_WITH,
        Conditions.Operator.NOT_STARTS_WITH,
        Conditions.Operator.ENDS_WITH,
        Conditions.Operator.NOT_ENDS_WITH,
      ],
    },
    {
      label: 'List',
      operators: [Conditions.Operator.IN, Conditions.Operator.NOT_IN],
    },
    {
      label: 'Compare',
      operators: [
        Conditions.Operator.GREATER_THAN,
        Conditions.Operator.GREATER_THAN_EQUALS,
        Conditions.Operator.LESS_THAN,
        Conditions.Operator.LESS_THAN_EQUALS,
      ],
    },
    {
      label: 'Existence',
      operators: [Conditions.Operator.IS_NULL, Conditions.Operator.IS_NOT_NULL],
    },
  ];

  private readonly textOperatorTooltips: Partial<Record<Conditions.Operator, string>> = {
    [Conditions.Operator.EQUALS]:
      'Value must exist and match exactly (case-insensitive). NULL or empty does not match.',
    [Conditions.Operator.NOT_EQUALS]:
      'Value must exist and not equal the target (case-insensitive). NULL or empty does not match.',
    [Conditions.Operator.CONTAINS]:
      'Value must exist and contain the substring (case-insensitive). NULL or empty does not match.',
    [Conditions.Operator.NOT_CONTAINS]:
      'Value must not contain the substring (case-insensitive). NULL or empty passes — the substring is not present.',
    [Conditions.Operator.STARTS_WITH]:
      'Value must exist and start with the substring (case-insensitive). NULL or empty does not match.',
    [Conditions.Operator.NOT_STARTS_WITH]:
      'Value must exist and not start with the substring (case-insensitive). NULL or empty does not match.',
    [Conditions.Operator.ENDS_WITH]:
      'Value must exist and end with the substring (case-insensitive). NULL or empty does not match.',
    [Conditions.Operator.NOT_ENDS_WITH]:
      'Value must not end with the substring (case-insensitive). NULL or empty passes — the substring is not at the end.',
    [Conditions.Operator.IN]:
      'Value must exist and match one list entry exactly (case-insensitive). Separate entries with commas (entries themselves cannot contain commas). The whole field is one value.',
    [Conditions.Operator.NOT_IN]:
      'Value must not exactly match any list entry (case-insensitive). Separate entries with commas. NULL or empty passes — the value is not in the list.',
    [Conditions.Operator.IS_NULL]: 'No value exists (NULL, undefined, or empty string).',
    [Conditions.Operator.IS_NOT_NULL]: 'Any value exists.',
    [Conditions.Operator.GREATER_THAN]: 'Lexicographic comparison; value must be a non-empty string.',
    [Conditions.Operator.GREATER_THAN_EQUALS]: 'Lexicographic comparison; value must be a non-empty string.',
    [Conditions.Operator.LESS_THAN]: 'Lexicographic comparison; value must be a non-empty string.',
    [Conditions.Operator.LESS_THAN_EQUALS]: 'Lexicographic comparison; value must be a non-empty string.',
  };

  booleanOperators = [
    Conditions.Operator.EQUALS,
    Conditions.Operator.NOT_EQUALS,
    Conditions.Operator.IS_NULL,
    Conditions.Operator.IS_NOT_NULL,
  ];

  form = this.formBuilder.group({
    propertyId: ['', [Validators.required, Validators.minLength(1)]],
    operator: ['', [Validators.required]],
    value: ['' as Conditions.PropertyValueType],
    type: [null as Conditions.PropertyType | null, Validators.required],
    valuePropertyId: [null as string | null],
  });

  onChange = (_value: Conditions.Rule<string | number | string[]>) => {};
  onTouched = () => {};

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  writeValue(condition: Conditions.Condition<Conditions.PropertyValueType> | null): void {
    if (condition) {
      this.form.patchValue({
        propertyId: condition.propertyId,
        operator: Object.values(Conditions.Operator).includes(condition.operator) ? condition.operator : null,
        value: condition.value,
        type: condition.type,
        valuePropertyId: condition.valuePropertyId ?? null,
      });
    }
  }

  registerOnChange(onChange: any): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  validate(): ValidationErrors | null {
    const operator = this.form.get('operator')?.value;

    let valueErrors = this.form.get('value')?.errors;
    if (
      valueErrors &&
      (operator === Conditions.Operator.CONTAINS ||
        operator === Conditions.Operator.NOT_CONTAINS ||
        operator === Conditions.Operator.STARTS_WITH ||
        operator === Conditions.Operator.NOT_STARTS_WITH ||
        operator === Conditions.Operator.ENDS_WITH ||
        operator === Conditions.Operator.NOT_ENDS_WITH ||
        operator === Conditions.Operator.IN ||
        operator === Conditions.Operator.NOT_IN)
    ) {
      const errors = Object.keys(valueErrors).filter(error => error !== 'phone' && error !== 'email');
      if (!errors.length) {
        valueErrors = null;
      }
    }

    const propertyErrors = this.form.get('propertyId')?.errors;
    const operatorErrors = this.form.get('operator')?.errors;

    return propertyErrors ||
      operatorErrors ||
      (valueErrors && operator !== this.isNotNull && operator !== this.isNull && !this.form.value.valuePropertyId)
      ? {
          propertyId: propertyErrors,
          operator: operatorErrors,
          value: valueErrors,
        }
      : null;
  }

  updateValue(value: boolean) {
    this.form.patchValue({ value: value });
  }

  removeCondition() {
    this.conditionRemoved.emit();
  }

  ngOnInit() {
    this.control = this.injector.get(NgControl);
    this.control.valueAccessor = this;
  }

  setPropertyId(
    property: { property: Property.Model<Property.PropertyType> | Conditions.Property; propertyPath: string } | null,
    clearValue: boolean
  ) {
    this.form.patchValue({
      propertyId: property?.propertyPath ?? null,
      ...(clearValue && { value: null, valuePropertyId: null, operator: Conditions.Operator.EQUALS }),
      // A missing placeholder's type may be a render-only TEXT fallback — keep the stored type.
      type: property
        ? isMissingProperty(property.property)
          ? (this.form.value.type ?? null)
          : property.property.type
        : null,
    });
    if (property) {
      this.form
        .get('value')
        ?.setValidators(getPropertyValidators(property.property as Property.Model<Property.PropertyType>, false));
    }

    this.selectedProperty = property?.property ?? null;
    this.selectedPropertyIsMissing = isMissingProperty(property?.property);
    this.loadCategoricalDimensionElements(property?.property ?? null);
  }

  // Categorical dimension conditions get a dropdown of the dimension's elements (id as
  // the stored value, title as the label) instead of free text — see setPropertyId.
  private loadCategoricalDimensionElements(
    property: Property.Model<Property.PropertyType> | Conditions.Property | null
  ) {
    const dimensionPrefix = `${DIMENSION_VALUE_NAMESPACE}.`;
    const isCategoricalDimension =
      !!property &&
      !isMissingProperty(property) &&
      property.type === this.text &&
      property.valueKey.startsWith(dimensionPrefix);

    this.selectedPropertyIsCategoricalDimension = isCategoricalDimension;
    this.categoricalDimensionElements = [];

    if (!isCategoricalDimension) return;

    const expectedValueKey = property.valueKey;
    const dimensionId = expectedValueKey.slice(dimensionPrefix.length);
    this.ratingMatrixSvc
      .listElements(dimensionId, { limit: 100 })
      .then(res => {
        if (this.selectedProperty?.valueKey !== expectedValueKey) return;
        this.categoricalDimensionElements = res.items ?? [];
      })
      .catch(() => {
        if (this.selectedProperty?.valueKey !== expectedValueKey) return;
        this.categoricalDimensionElements = [];
      });
  }

  setValuePropertyId(
    property: { property: Property.Model<Property.PropertyType> | Conditions.Property; propertyPath: string } | null
  ) {
    this.form.patchValue({
      valuePropertyId: property?.propertyPath ?? null,
      value: null,
    });
  }

  textOperatorTooltip(operator: Conditions.Operator): string {
    return this.textOperatorTooltips[operator] ?? operator;
  }
}
